// Copyright (c) 2022 by Move Closer

import { CartItemProps } from '../../../front/checkout/molecules/CartItem/CartItem.contracts'

import { HasIdentifier } from '../../shared'
import { VariantSneakPeak } from '../../products'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export type OrderStatus = string

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export enum ShippingMethod {
  DhlPop = 'DHP POP',
  DhlPopPobranie = 'DHL POP - pobranie',
  DPD = 'DPD POLSKA',
  InPost = 'inPost'
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * TODO: MOVE to config (LIRENE) - Application should specify available statuses in config.modules
 * @see config.modules.possibleOrderStatuses
 * @see config.modules.highlightedOrderStatuses
 * @see config.modules.retryOrderStatuses
 * @see config.modules.returnOrderStatuses
 * TODO: END
 */
export const PossibleOrderStatus: Record<OrderStatus, string> = {
  Cancelled: 'canceled',
  Closed: 'closed',
  Complete: 'complete',
  Processing: 'processing',
  Pending: 'pending',
  'Pending payment': 'pendingPayment',
  'Payment Review': 'paymentReview',
  'On hold': 'onHold',
  ' zamknięte': 'zamkniete',
  'Nowe zamówienie': 'noweZamowienie',
  'Przyjęte do realizacji': 'przyjeteDoRealizacji'
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const shippingMethodsIconsRegistry: Record<string, string> = {
  [ShippingMethod.DhlPop]: 'DHL',
  [ShippingMethod.DhlPopPobranie]: 'DHL',
  [ShippingMethod.DPD]: 'DPD',
  [ShippingMethod.InPost]: 'Inpost'
}

export interface OrderPaymentMethod {
  name: string
  type: string
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Filip Rurak <filip.rurak@movecloser.pl> (edited)
 */
export interface OrderData extends AmastyGiftCards, HasIdentifier {
  billingAddress: OrderAddress
  carrier: string
  createdAt: string
  milesAndMore?: string
  milesAndMorePoints?: number
  paymentMethod: string
  paymentMethods: OrderPaymentMethod[]
  products: ProductOrderData[]
  productSkus: VariantSneakPeak<string>[]
  productsCost: number
  shippingAddress: OrderAddress
  shippingCost: number
  shippingMethod: string
  status: OrderStatus
  statusCode: OrderStatus
  statusDetails: OrderStatus
  total: OrderTotal
  totalCost: number
  updatedAt: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface ShippingHandling {
  amountIncludingTax: OrderValueDescription
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrderTotal {
  discounts?: Array<OrderDiscount>
  grandTotal: OrderValueDescription
  shippingHandling: ShippingHandling
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrdersResponse {
  orders: Array<OrderData>
  totalCount: number
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrderProductsPrice {
  value: number
  currency: number
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface ProductOrderData {
  name: string
  price: OrderProductsPrice
  quantity: number
  sku: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrderCosts {
  products: number
  shipping: number
  total: number
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrderAddress {
  city: string
  company: string
  countryCode: string
  fax: string
  firstName: string
  lastName: string
  middleName: string
  postCode: string
  prefix: string
  region: string
  regionId: string
  street: Array<string>
  suffix: string
  telephone: string
  vatId: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrderAddressesData {
  billing: OrderAddress
  shipping: OrderAddress
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrderValueDescription {
  currency: string
  value: number
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrderDiscount {
  amount: OrderValueDescription
  label: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface SingleOrderData extends AmastyGiftCards, HasIdentifier {
  addresses: OrderAddressesData
  costs: OrderCosts
  createdAt: string
  discounts?: Array<OrderDiscount>
  milesAndMore?: string
  milesAndMorePoints?: number
  paymentMethod?: PaymentMethod | string
  paymentMethods: OrderPaymentMethod[]
  products: ProductOrderData[]
  shippingMethod: string
  status: OrderStatus
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface AmastyGiftCards {
  appliedAmastyGiftCards?: Array<AppliedAmastyGiftCard>
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface AppliedAmastyGiftCard {
  code: string
  value: number
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface PaymentMethod {
  active: boolean
  id: number
  imgUrl: string
  mobileImgUrl: string
  name: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface RetryPaymentUrl {
  url: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export type MappedOrder = Omit<SingleOrderData, 'products'> & { products: Array<CartItemProps & { sku?: string }> }

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const ORDERS_VIEW_KEY = 'OrdersView'

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface OrdersViewConfig {
  hasMargin: boolean
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const ORDERS_VIEW_CONFIG_MAP: OrdersViewConfig = {
  hasMargin: true
}
